// GuruCloudAI Feedback Widget - loads the embeddable widget for admin users
// Submissions create tasks on the ChemMasters kanban board in GuruCloudAI
//
// The widget script loads at most once per page lifetime and only after the
// authenticated user is known — see guruWidgetLoader.ts for why (the widget
// freezes its notification identity at init and cannot be re-registered).

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { syncGuruWidgetMetadata, clearGuruWidgetMetadata } from '../../utils/guruWidgetMetadata';
import { ensureGuruWidget, setGuruNativeButtonHidden } from '../../utils/guruWidgetLoader';

export function GuruFeedbackWidget() {
  // The widget follows the real person at the keyboard: while an admin uses
  // "View as", that is the admin (so the widget stays up on every page), and
  // the viewed account rides along as viewed_as_* metadata.
  const { user, realUser } = useAuth();
  const location = useLocation();
  const userId = realUser?.id ?? null;
  const viewedAs = realUser && user && user.id !== realUser.id ? user : null;

  // Keep window.GuruWidgetMetadata in sync with user + page.
  // Declared before the loader effect so metadata exists before the script.
  useEffect(() => {
    syncGuruWidgetMetadata(realUser, 'admin', location.pathname, location.search, viewedAs);
  }, [realUser, viewedAs, location.pathname, location.search]);

  // Don't leave identity metadata behind on pages without a widget embed.
  useEffect(() => {
    return () => {
      clearGuruWidgetMetadata();
    };
  }, []);

  useEffect(() => {
    const status = ensureGuruWidget(userId, {
      color: '#dc3545',
      title: 'Request a Feature or Report a Bug',
    });
    // Native launcher is only shown for the user the widget is bound to;
    // an unauthenticated or mismatched session must not see (or ack) another
    // identity's notifications.
    setGuruNativeButtonHidden(status !== 'active');

    return () => {
      setGuruNativeButtonHidden(true);
    };
  }, [userId]);

  return null;
}
